import React, { useState, useEffect, useRef } from 'react';
import ReactConfetti from 'react-confetti';
import EosLogo from '../layout/EosLogo';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../Company/CompanyContext';

function CelebrationModal({ isOpen, onClose, userName }) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [blurApplied, setBlurApplied] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const [confettiActive, setConfettiActive] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const modalRef = useRef(null);
  const navigate = useNavigate();
  
  const { companyInfo } = useCompany();
  
  // Debug when isOpen changes
  useEffect(() => {
    console.log("CelebrationModal isOpen changed:", isOpen);
  }, [isOpen]);
  
  // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle actual rendering, blur, and visibility with proper animation timing
  useEffect(() => {
    let blurTimer, visibilityTimer, backdropTimer, renderTimer, confettiTimer;
    
    if (isOpen) {
      console.log("CelebrationModal opening animation starting");
      // First render the component
      setShouldRender(true);
      
      // Apply blur immediately but keep opacity at 0
      blurTimer = setTimeout(() => {
        setBlurApplied(true);
        
        // Wait for blur to be fully applied before showing backdrop
        backdropTimer = setTimeout(() => {
          setBackdropVisible(true);
          
          // Only after backdrop and blur are ready, fade in content
          visibilityTimer = setTimeout(() => {
            setIsVisible(true);
            
            // Start confetti after modal appears
            confettiTimer = setTimeout(() => {
              setConfettiActive(true);
              console.log("CelebrationModal confetti activated");
            }, 300);
          }, 200);
        }, 100);
      }, 10);
    } else {
      console.log("CelebrationModal closing animation starting");
      // First stop confetti
      setConfettiActive(false);
      
      // Fade out content
      setIsVisible(false);
      
      // After content fades, start fading backdrop
      backdropTimer = setTimeout(() => {
        setBackdropVisible(false);
        
        // Then remove blur effect
        blurTimer = setTimeout(() => {
          setBlurApplied(false);
          
          // Finally remove from DOM after all animations complete
          renderTimer = setTimeout(() => {
            setShouldRender(false);
            console.log("CelebrationModal completely removed from DOM");
          }, 100);
        }, 150);
      }, 300);
    }
    
    // Cleanup all timers on unmount
    return () => {
      clearTimeout(blurTimer);
      clearTimeout(visibilityTimer);
      clearTimeout(backdropTimer);
      clearTimeout(renderTimer);
      clearTimeout(confettiTimer);
    };
  }, [isOpen]);
  
  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Handle custom close with animation
  const handleClose = () => {
    // Step 1: Stop confetti
    setConfettiActive(false);
    
    // Step 2: Fade out the card
    setIsVisible(false);
    
    // Step 3: After card fades, fade out backdrop
    setTimeout(() => {
      setBackdropVisible(false);
      
      // Step 4: After backdrop fades, remove blur
      setTimeout(() => {
        setBlurApplied(false);
        
        // Step 5: Finally notify parent component after all animations finish
        setTimeout(() => {
          onClose();
        }, 150);
      }, 150);
    }, 300);
  };
  
  // Handle click on Let's Go button - navigate to main page
  const handleLetsGo = () => {
    handleClose();
    navigate('/');
  };
  
  if (!shouldRender) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]">
      {/* Confetti over entire screen */}
      {confettiActive && (
        <ReactConfetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.2}
          colors={['#FFA500', '#FF8C00', '#FF4500', '#FFD700', '#FFFF00', '#F0E68C']}
          tweenDuration={5000}
        />
      )}
      
      {/* Backdrop blur and slight darkening */}
      <div 
        className={`absolute inset-0 transition-all duration-500 ease-out ${
          blurApplied ? 'backdrop-filter backdrop-blur-md' : 'backdrop-filter backdrop-blur-none'
        } ${
          backdropVisible ? 'bg-gray-900 bg-opacity-30' : 'bg-opacity-0'
        }`}
        onClick={handleClose}
        style={{ transitionProperty: 'backdrop-filter, background-color, opacity' }}
      />
      
      {/* Glassmorphism modal with extra blur directly behind it */}
      <div 
        ref={modalRef}
        className={`w-full max-w-md mx-4 relative 
        bg-white bg-opacity-25 border border-white border-opacity-30
        shadow-xl rounded-xl backdrop-filter backdrop-blur-md
        transition-all duration-500 ease-out transform
        ${isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}
        style={{
          boxShadow: blurApplied ? '0 0 60px 25px rgba(255,255,255,0.25), 0 8px 25px rgba(0,0,0,0.12)' : 'none'
        }}
      >
        <div className="p-6 text-center">
          {/* Close button */}
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 bg-white bg-opacity-50 rounded-full p-1.5 
              hover:bg-opacity-80 transition-all duration-200 transform hover:rotate-90 btn-pop"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="flex justify-center mb-6">
            <div className="transition-transform duration-300 transform hover:scale-110 w-44 h-22 flex items-center justify-center p-2 bg-white bg-opacity-30 rounded-lg shadow-sm">
              <EosLogo customLogoUrl={companyInfo?.customLogoUrl} />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 font-heading mb-3">Welcome to EOS AI!</h2>
          
          <p className="text-lg text-gray-700 mb-5">
            {userName ? `Congratulations ${userName}!` : 'Congratulations!'} Your company is now set up and ready to go.
          </p>
          
          <p className="text-gray-600 mb-6">
            Get ready to transform your business with the Entrepreneurial Operating System®.
          </p>
          
          <button
            onClick={handleLetsGo}
            className="py-2 px-6 bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-eos-orange hover:shadow-eos-lg 
            transition-all duration-300 flex items-center btn-pop mx-auto"
          >
            Let's Go!
          </button>
        </div>
      </div>
    </div>
  );
}

export default CelebrationModal; 